import type { Game, Prediction } from './types'

export function hasNoPredictions(
  games: Game[],
  predictions: Record<string, Prediction>
): boolean {
  return games.length > 0 && games.every((game) => !predictions[game.id])
}

// The "Pick your Banker" nudge should stay quiet on a totally untouched
// gameweek (the "Predict Now" CTA owns that screen) and only start nagging
// once the player has made their first prediction edit without setting one.
// It also has to stop once the deadline passes — predictions and
// banker selection are locked by then, so the live nudge hands off to
// shouldShowMissedBankerRow instead of surviving as a dead call to action.
export function shouldShowBankerNudge(
  games: Game[],
  predictions: Record<string, Prediction>,
  bankerGameId: string | null,
  deadlinePassed: boolean
): boolean {
  return (
    !deadlinePassed &&
    bankerGameId === null &&
    !hasNoPredictions(games, predictions)
  )
}

// Post-deadline counterpart to shouldShowBankerNudge: once the
// deadline passes with no Banker ever set, the live nudge is replaced by a
// monochrome "Banker was not selected" row communicating the past fact. Same
// participation gating as the live nudge, so a gameweek the player never
// opened doesn't get a spurious message on top of its own empty state.
export function shouldShowMissedBankerRow(
  games: Game[],
  predictions: Record<string, Prediction>,
  bankerGameId: string | null,
  deadlinePassed: boolean
): boolean {
  return (
    deadlinePassed &&
    bankerGameId === null &&
    !hasNoPredictions(games, predictions)
  )
}

// Gates the "GW {N} Calls" reveal section and the chip tiles on another
// player's profile — visible from the
// gameweek's own deadline passing until the app advances to the next gameweek.
// Before the deadline, a rival's picks could still inform the viewer's own.
// The window used to shut again at finalization, which left the settled evening
// before rollover showing a played chip as unplayed; it now closes
// on its own, because the next gameweek's deadline has not passed.
export function canRevealOtherPlayerGameweek(
  gameweek: number,
  deadlinePassed: boolean
): boolean {
  return gameweek > 0 && deadlinePassed
}

export function getNextCycleGameId(
  games: Game[],
  currentGameId: string
): string | null {
  const currentIndex = games.findIndex((game) => game.id === currentGameId)
  if (currentIndex === -1) return null
  return games[currentIndex + 1]?.id ?? null
}
